import React, { useState } from 'react';
import moment from 'moment';
import {
  StopTravelStatus,
  AlarmEventType,
  VehicleScheduleState,
  VehicleState,
  SystemState,
  TaskTypeText,
} from '../../redux/type';
import { stringToEnum, translateMileageNum } from '../../utils/util';
import { Tooltip, message } from 'antd';
import { cloneDeep } from 'lodash';
import { enumTranslator } from '@/utils';
import copyIcon from '@/assets/img/copy.png';
import { convertSpeedMsToKmh } from '@/utils/util';

export const formatRealTimeData = (data: any) => {
  const {
    latitude,
    longitude,
    power,
    steerZero,
    sceneSignal,
    scheduleState,
    speed,
    taskType,
    globalMileage,
    currentScheduleFinishedMileage,
    inverseAddress,
    stationName,
    androidVersion,
    roverVersion,
    videoVersion,
    videoCloudVersion,
    androidCloudVersion,
    roverCloudVersion,
    mapVersion,
    stopList,
  } = data || {};
  const _stopListReverse = cloneDeep(stopList).reverse();
  const stayPoint = _stopListReverse.find(
    (item: StopItem) => item.travelStatus === StopTravelStatus.STAY,
  );

  const finalDepartPoint = _stopListReverse.find(
    (item: StopItem) => item.travelStatus === StopTravelStatus.DEPART,
  );
  const [copied, setCopied] = useState(false);

  const fieldsGroup: any[] = [
    {
      title: '业务及行驶状态',
      fields: [
        {
          label: '业务类型',
          key: 'taskType',
          value: stringToEnum(TaskTypeText, taskType) || '--',
        },
        {
          label: '配送状态',
          key: 'scheduleState',
          value: enumTranslator('SCHEDULE_STATE_ENUM', scheduleState) || '--',
        },
        {
          label: '站点',
          key: 'stationName',
          value: stationName,
        },
        stayPoint
          ? {
              label: (
                <div>
                  <div>到达时间</div>
                  <div>(当前点)</div>
                </div>
              ),
              key: 'arrivedTime',
              value: stayPoint?.arrivedTime?.substring(11),
            }
          : finalDepartPoint
            ? {
                label: (
                  <div>
                    <div>出发时间</div>
                    <div>(上一点)</div>
                  </div>
                ),
                key: 'arrivedTime',
                value: finalDepartPoint?.departTime?.substring(11),
              }
            : false,
      ],
    },
    {
      fields: [
        {
          label: '剩余电量',
          key: 'power',
          value: power?.toFixed(0),
          unit: '%',
        },
        {
          label: '速度',
          key: 'speed',
          value: convertSpeedMsToKmh(speed, 1),
        },
        {
          label: '里程信息',
          key: 'mileage',
          value: `${translateMileageNum(
            currentScheduleFinishedMileage,
          )}/${translateMileageNum(globalMileage)} km`,
        },
        {
          label: '零偏值',
          key: 'steerZero',
          value: steerZero,
          unit: '°',
        },
      ],
    },
    {
      fields: [
        {
          label: '经度位置',
          key: 'lon',
          value: longitude,
          icon: longitude ? (
            <img
              src={copyIcon}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(longitude.toString());
                  setCopied(true);
                  message.success('复制成功');
                } catch (error) {
                  message.error('无法复制到剪贴板:' + error);
                }
              }}
            />
          ) : null,
        },
        {
          label: '纬度位置',
          key: 'lat',
          // id: 'clipBoard',
          value: latitude,
          icon: latitude ? (
            <img
              src={copyIcon}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(latitude.toString());
                  setCopied(true);
                  message.success('复制成功');
                } catch (error) {
                  message.error('无法复制到剪贴板:' + error);
                }
              }}
            />
          ) : null,
        },

        {
          label: '定位地址',
          value: inverseAddress,
          key: 'inverseAddress',
        },
        {
          label: '定位置信度',
          key: 'sceneSignal',
          value: sceneSignal?.toFixed(2),
        },
      ],
    },
    {
      fields: [
        {
          label: 'Rover版本',
          key: 'roverVersion',
          value: roverVersion,
          icon: roverCloudVersion && roverCloudVersion !== roverVersion && (
            <Tooltip title={`待升级版本${roverCloudVersion}`}>
              <i className='tip-info'>!</i>
            </Tooltip>
          ),
        },
        {
          label: '安卓版本',
          key: 'androidVersion',
          value: androidVersion,
          icon: androidCloudVersion &&
            androidCloudVersion !== androidVersion && (
              <Tooltip title={`待升级版本${androidCloudVersion}`}>
                <i className='tip-info'>!</i>
              </Tooltip>
            ),
        },
        {
          label: '视频版本',
          value: videoVersion,
          key: 'videoVersion',
          icon: videoCloudVersion && videoCloudVersion !== videoVersion && (
            <Tooltip title={`待升级版本${videoCloudVersion}`}>
              <i className='tip-info'>!</i>
            </Tooltip>
          ),
        },
        {
          label: '地图版本',
          key: 'mapVersion',
          value: mapVersion,
        },
      ],
    },
  ];
  return fieldsGroup;
};

export const formatVehicleDetail = (data: any) => {
  const { alarmEventList, systemState, vehicleState } = data || {};
  const fieldsGroup: any[] = [
    {
      title: '车辆状态',
      fields: [
        {
          label: '报警事件',
          key: 'alarmList',
          value: alarmEventList
            ?.map((item: any) =>
              enumTranslator('ALARM_EVENT_ENUM', item.alarmEvent),
            )
            .join(','),
        },
      ],
    },
    {
      fields: [
        {
          label: '系统状态',
          key: 'systemState',
          value: enumTranslator('SYSTEM_STATE_ENUM', systemState) || '--',
        },
      ],
    },
    {
      fields: [
        {
          label: '车辆模式',
          key: 'vehicleState',
          value: enumTranslator('VEHICLE_STATE_ENUM', vehicleState) || '--',
        },
      ],
    },
  ];

  return fieldsGroup;
};

export const columns: any = [
  {
    title: '指令名称',
    dataIndex: 'eventTypeName',
    align: 'left',
    key: 'eventTypeName',
  },
  {
    title: '指令内容',
    dataIndex: 'content',
    align: 'left',
    key: 'content',
    width: 400,
  },
  {
    title: '发生时间',
    dataIndex: 'reportTime',
    key: 'reportTime',
    align: 'left',
    render: (text: string) => {
      return moment.utc(text).format('YYYY-MM-DD HH:mm:ss');
    },
  },
  {
    title: '指令负责人',
    dataIndex: 'maintainerUser',
    key: 'maintainerUser',
    align: 'left',
    render: (text: any) => text,
  },
  {
    title: '指令接收人',
    dataIndex: 'followUser',
    key: 'followUser',
    align: 'left',
    render: (text: any) => text,
  },
];

/**
 * 把毫秒格式化
 * @param {number} time 毫秒
 * @return {string}
 */
export const formatTime = (time: number | undefined) => {
  if (!time && typeof time !== 'number') {
    return '';
  }
  if (time <= 0) {
    return '00:00:00';
  }
  const hours = Math.floor(time / 1000 / 3600);
  const minutes = Math.floor(time / 1000 / 60) % 60;
  const seconds = Math.floor((time / 1000) % 60);

  function format(v: number) {
    return v >= 10 ? v : `0${v}`;
  }

  return `${format(hours)}:${format(minutes)}:${format(seconds)}`;
};

export const getPosPercent = (duration: number, currentTime: number) => {
  return (currentTime * 100) / (duration || -1);
};

export const isInRange = (time: number, start: number, end: number): boolean =>
  time >= start && time <= end;
export const getTimeScale = (
  currentTime: number,
  startTime: number,
  endTime: number,
) => {
  const isActiveTime = isInRange(currentTime, startTime, endTime);
  const timeDiff = endTime - startTime;
  const timeDiffWithCurrent = currentTime - startTime;
  const currentScalePercent = isActiveTime ? timeDiffWithCurrent / timeDiff : 0;

  return currentScalePercent;
};

export const getNewStartTime = (
  originStartTime: string,
  currentTime: number,
) => {
  const originStartTimeUnix = new Date(originStartTime).getTime();
  const timeDiffWithCurrent = originStartTimeUnix + currentTime;
  return new Date(timeDiffWithCurrent).toString();
};
